/**
 * ReplyDisplay Component
 * Shows quoted reply header above message bubble (like iMessage)
 * Displays sender name + content snippet
 * Clickable to scroll to original message with highlight animation
 */

import type { Message } from '@/types/socketEvents'

interface ReplyDisplayProps {
  replyTo: Message['reply_to']
  onClickReply: (messageId: string) => void
  isSentByMe: boolean // For styling alignment
}

export function ReplyDisplay({ replyTo, onClickReply, isSentByMe }: ReplyDisplayProps) {
  if (!replyTo) return null

  const senderName = replyTo.sender?.username || 'Unknown'
  const hasAttachments = replyTo.attachments && replyTo.attachments.length > 0
  const hasContent = replyTo.content && replyTo.content.trim().length > 0

  // Get preview text
  const getPreviewText = () => {
    if (hasContent) {
      return replyTo.content.length > 50
        ? replyTo.content.substring(0, 50) + '...'
        : replyTo.content
    }

    if (hasAttachments && replyTo.attachments) {
      const attachment = replyTo.attachments[0]
      const type = attachment.type || 'file'

      if (type === 'image') return '📷 Photo'
      if (type === 'video') return '🎥 Video'
      if (type === 'audio') return '🎵 Audio'
      return '📎 Attachment'
    }

    return '[Message deleted]'
  }

  return (
    <div
      onClick={() => onClickReply(replyTo.id)}
      className="mb-2 cursor-pointer hover:opacity-80 transition-opacity"
      aria-label={`Jump to reply from ${senderName}`}
    >
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-l-4 ${
          isSentByMe
            ? 'bg-blue-50 border-braydin-olive'
            : 'bg-gray-100 border-gray-400'
        }`}
      >
        {/* Text content */}
        <div className="flex-1 min-w-0">
          {senderName !== 'Unknown' && (
            <div className="text-xs font-semibold text-gray-600">
              {senderName}
            </div>
          )}
          <div className="text-xs text-gray-500 truncate">
            {getPreviewText()}
          </div>
        </div>

        {/* Arrow icon */}
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
        </svg>
      </div>
    </div>
  )
}
